// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';

import { isNotNil } from 'helpers/isNotNil';
import type { BendPreviewSpecifications } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import { getTangentDistance } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import {
  buildLabelOpts,
  type BendDefLabel,
  type LabelOpts,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/buildLabelOpts';

/**
 * Specifies whether a segment length is measured from/to the centerline vertex
 * or the outer tube edge (the physical face of the conduit).
 *
 * - `'vertex'` (default): `runLength` is a vertex-to-vertex centerline distance. Use this for
 *   intermediate segments and for YBC/layout geometries that naturally work in centerline space.
 * - `'tubeEdge'`: `runLength` includes the conduit radius at that end. The framework automatically
 *   subtracts `conduitSize / 2` so the internal centerline vertex lands in the correct position.
 *   Use this for the start of the first segment and the end of the last segment on bends where
 *   real-world measurements are taken from the physical tube face.
 */
export type SegmentEndpoint = 'vertex' | 'tubeEdge';

/**
 * A representation of one segment of a bend: run `runLength` feet in the current direction,
 * then optionally rotate and bend it.
 */
export type BendDef = {
  /**
   * How long to run in the current direction.
   * The measurement space at each end is controlled by `startMeasuredFrom` and `endMeasuredTo`.
   */
  runLength: number;
  /**
   * Specifies the measurement space at the start of this segment. @default 'vertex'
   */
  startMeasuredFrom?: SegmentEndpoint;
  /**
   * Specifies the measurement space at the end of this segment. @default 'vertex'
   * Set to `'tubeEdge'` on the last segment of a bend when `runLength` is measured
   * to the physical tube face rather than the centerline end vertex.
   */
  endMeasuredTo?: SegmentEndpoint;
  /** If present, the rotation in radians to apply to the roll axis after running but before bending.
   * Corresponds to the B column in YBC.
   **/
  thenRotateBy?: number;
  /** If present, the bend angle in radians to apply in the pitch axis after running.
   *  Corresponds to the C column in YBC.
   */
  thenBendBy?: number;
  /** Labels associated with this bend segment, placed relative to the start of the bend. */
  startLabels?: readonly BendDefLabel[];
  /** Labels associated with this bend segment, placed relative to the end of the bend. */
  endLabels?: readonly BendDefLabel[];
};

/**
 * Converts a list of bend definitions into a curve path and labels.
 * @param dimensions The dimensions of the bend.
 * @param bendDefs The bend definitions to use.
 */
export const buildBendDataFromBendDef = (dimensions: BendPreviewSpecifications, bendDefs: readonly BendDef[]) => {
  const labelOpts: LabelOpts[] = [];
  const path = new THREE.CurvePath<THREE.Vector3>();
  // Used to track the current orientation of the bend as we apply the bendDefs sequentially.
  // We also use this to track the position of the last bend vertex.
  const bendToolSim = new THREE.Object3D();
  // Used for carryover of the length of the last bend segment.
  let distanceBetweenVertexAndLastPoint = 0;

  bendDefs.forEach((bendPart, i, arr) => {
    const forwardBeforeBend = bendToolSim.getWorldDirection(new THREE.Vector3()).normalize();
    const startVertex = bendToolSim.position.clone();

    // Automatically correct for real-world measurements that include the conduit radius at a tube face.
    // When startMeasuredFrom or endMeasuredTo is 'tubeEdge', subtract conduitSize / 2 from the
    // effective run length so the internal centerline vertex is placed correctly.
    // On the first segment of a bend, the starting tube edge and vertex will be in the same place,
    // and on the last segment, the ending tube edge and vertex will be in the same place.
    const conduitRadius = dimensions.conduitSize / 2;
    const startTrim = bendPart.startMeasuredFrom === 'tubeEdge' && i > 0 ? conduitRadius : 0;
    const endTrim = bendPart.endMeasuredTo === 'tubeEdge' && i < arr.length - 1 ? conduitRadius : 0;
    const effectiveRunLength = bendPart.runLength - startTrim - endTrim;

    // Our lengths are vertex to vertex, not bend start to bend end.
    // So to get the measurements to line up, we need to know the bend vertices, which might not actually be on the bend.
    const startPoint = startVertex
      .clone()
      .add(forwardBeforeBend.clone().multiplyScalar(distanceBetweenVertexAndLastPoint));
    const endVertex = startVertex.clone().add(forwardBeforeBend.clone().multiplyScalar(effectiveRunLength));

    // Handle any labels that are placed relative to the start of the bend.
    if (isNotNil(bendPart.startLabels)) {
      bendPart.startLabels.forEach((labelDef) => {
        labelOpts.push(
          buildLabelOpts(labelDef, {
            materialRadius: conduitRadius,
            bendToolSim,
          }),
        );
      });
    }

    let theta = 0;
    if (isNotNil(bendPart.thenRotateBy)) {
      // This simulates rotating the conduit around the "roll" axis. This axis is aligned with "forward",
      // So it won't affect the forward direction but will affect the "up" direction, which is the plane the bend will be in.
      bendToolSim.rotateOnAxis(new THREE.Vector3(0, 0, 1), bendPart.thenRotateBy);
    }

    if (isNotNil(bendPart.thenBendBy)) {
      // This simulates bending the conduit. The bend is along the simulator's "up" direction.
      // If we're bending, we'll need to place a Bézier curve.
      bendToolSim.rotateOnAxis(new THREE.Vector3(-1, 0, 0), bendPart.thenBendBy);
      const forwardAfterBend = bendToolSim.getWorldDirection(new THREE.Vector3()).normalize();
      // Find the angle between current direction and the new direction after rotation
      // Use it to get the distance from the intersection (bend center) to the tangent points
      theta = forwardBeforeBend.angleTo(forwardAfterBend);
      const tangentDistance = getTangentDistance(dimensions.bendRadius, theta);

      const bendStartPoint = endVertex.clone().sub(forwardBeforeBend.multiplyScalar(tangentDistance));
      path.add(new THREE.LineCurve3(startPoint, bendStartPoint));

      const bendEndPoint = endVertex.clone().add(forwardAfterBend.multiplyScalar(tangentDistance));
      path.add(new THREE.QuadraticBezierCurve3(bendStartPoint, endVertex, bendEndPoint));
      // Carry the tangent length into the next segment.
      distanceBetweenVertexAndLastPoint = tangentDistance;
    } else {
      path.add(new THREE.LineCurve3(startPoint, endVertex));
      distanceBetweenVertexAndLastPoint = 0;
    }

    bendToolSim.position.copy(endVertex);

    // Handle any labels that are placed relative to the end of the bend.
    if (isNotNil(bendPart.endLabels)) {
      bendPart.endLabels.forEach((labelDef) => {
        labelOpts.push(
          buildLabelOpts(labelDef, {
            materialRadius: conduitRadius,
            bendToolSim,
          }),
        );
      });
    }
  });

  return { path, labelOpts };
};
